const clampNumber = (value, min, max, fallback) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }
  return Math.max(min, Math.min(max, numeric));
};
const MAX_SHAPE_DATA_URL_CHARS = 2 * 1024 * 1024;

const sanitizeId = (value, fallback) => {
  const raw = String(value || '').trim().toLowerCase();
  const normalized = raw.replace(/[^a-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
  return normalized || fallback;
};

const sanitizeLabel = (value, fallback) => {
  const text = String(value || '').trim();
  return text || fallback;
};

const normalizeShapeDataUrl = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  const dataUrl = value.trim();
  if (!dataUrl.startsWith('data:image/png;base64,')) {
    return null;
  }
  if (dataUrl.length > MAX_SHAPE_DATA_URL_CHARS) {
    return null;
  }
  return dataUrl;
};

const normalizeShapeBounds = (value) => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const width = Number(value.width);
  const height = Number(value.height);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  return {
    width: Math.round(width),
    height: Math.round(height),
  };
};

const DEFAULT_TWO_D_PRESETS = [
  {
    id: 'rectangular-2d-plain-cut-80x60x20',
    label: 'Rectangular 2D Plain Cut - Small (80 x 60 x 20 mm)',
    shape: 'rectangular-2d-plain-cut',
    widthMm: 80,
    heightMm: 60,
    dpi: 600,
  },
  {
    id: 'rectangular-2d-plain-cut-90x130x20',
    label: 'Rectangular 2D Plain Cut - Medium (90 x 130 x 20 mm)',
    shape: 'rectangular-2d-plain-cut',
    widthMm: 90,
    heightMm: 130,
    dpi: 600,
  },
  {
    id: 'rectangular-2d-plain-cut-100x150x20',
    label: 'Rectangular 2D Plain Cut - Big (100 x 150 x 20 mm)',
    shape: 'rectangular-2d-plain-cut',
    widthMm: 100,
    heightMm: 150,
    dpi: 600,
  },
  {
    id: 'diamond-cut-heart-2d-90x90x20',
    label: 'Diamond Cut Heart 2D - Medium (90 x 90 x 20 mm)',
    shape: 'diamond-cut-heart-2d',
    widthMm: 90,
    heightMm: 90,
    dpi: 600,
  },
  {
    id: 'diamond-cut-heart-2d-140x140x20',
    label: 'Diamond Cut Heart 2D - Big (140 x 140 x 20 mm)',
    shape: 'diamond-cut-heart-2d',
    widthMm: 140,
    heightMm: 140,
    dpi: 600,
  },
  {
    id: 'octagon-plaque-100x100x20',
    label: 'Octagon Plaque - Medium (100 x 100 x 20 mm)',
    shape: 'octagon-plaque',
    widthMm: 100,
    heightMm: 100,
    dpi: 600,
  },
  {
    id: 'octagon-plaque-180x180x20',
    label: 'Octagon Plaque - Big (180 x 180 x 20 mm)',
    shape: 'octagon-plaque',
    widthMm: 180,
    heightMm: 180,
    dpi: 600,
  },
  {
    id: 'rectangular-block-3d-50x80x50',
    label: 'Rectangular Block 3D - Small (50 x 80 x 50 mm)',
    shape: 'rectangular-block-3d',
    widthMm: 50,
    heightMm: 80,
    dpi: 600,
  },
  {
    id: 'rectangular-block-3d-120x80x50',
    label: 'Rectangular Block 3D - Medium (120 x 80 x 50 mm)',
    shape: 'rectangular-block-3d',
    widthMm: 120,
    heightMm: 80,
    dpi: 600,
  },
  {
    id: 'rectangular-block-3d-180x130x50',
    label: 'Rectangular Block 3D - Big (180 x 130 x 50 mm)',
    shape: 'rectangular-block-3d',
    widthMm: 180,
    heightMm: 130,
    dpi: 600,
  },
  {
    id: 'square-block-3d-60x60x60',
    label: 'Square Block 3D - Small (60 x 60 x 60 mm)',
    shape: 'square-block-3d',
    widthMm: 60,
    heightMm: 60,
    dpi: 600,
  },
  {
    id: 'square-block-3d-100x100x60',
    label: 'Square Block 3D - Medium (100 x 100 x 60 mm)',
    shape: 'square-block-3d',
    widthMm: 100,
    heightMm: 100,
    dpi: 600,
  },
  {
    id: 'square-block-3d-140x140x60',
    label: 'Square Block 3D - Big (140 x 140 x 60 mm)',
    shape: 'square-block-3d',
    widthMm: 140,
    heightMm: 140,
    dpi: 600,
  },
  {
    id: 'iceberg-130x100x50',
    label: 'Iceberg - Medium (130 x 100 x 50 mm)',
    shape: 'iceberg',
    widthMm: 130,
    heightMm: 100,
    dpi: 600,
  },
  {
    id: 'iceberg-175x135x50',
    label: 'Iceberg - Big (175 x 135 x 50 mm)',
    shape: 'iceberg',
    widthMm: 175,
    heightMm: 135,
    dpi: 600,
  },
  {
    id: 'regular-heart-3d-90x90x30',
    label: 'Regular Heart 3D - Medium (90 x 90 x 30 mm)',
    shape: 'regular-heart-3d',
    widthMm: 90,
    heightMm: 90,
    dpi: 600,
  },
  {
    id: 'regular-heart-3d-140x140x50',
    label: 'Regular Heart 3D - Big (140 x 140 x 50 mm)',
    shape: 'regular-heart-3d',
    widthMm: 140,
    heightMm: 140,
    dpi: 600,
  },
  {
    id: 'circle-block-3d-140x120x40',
    label: 'Circle Block 3D - One Size (140 x 120 x 40 mm)',
    shape: 'circle-block-3d',
    widthMm: 140,
    heightMm: 120,
    dpi: 600,
  },
];

const DEFAULT_THREE_D_PRESETS = [
  {
    id: 'rectangular-block-3d-50x80x50',
    label: 'Rectangular Block 3D - Small (50 x 80 x 50 mm)',
    shape: 'rectangular-block-3d',
    widthMm: 50,
    heightMm: 80,
    depthMm: 50,
    dpi: 600,
  },
  {
    id: 'rectangular-block-3d-120x80x50',
    label: 'Rectangular Block 3D - Medium (120 x 80 x 50 mm)',
    shape: 'rectangular-block-3d',
    widthMm: 120,
    heightMm: 80,
    depthMm: 50,
    dpi: 600,
  },
  {
    id: 'rectangular-block-3d-180x130x50',
    label: 'Rectangular Block 3D - Big (180 x 130 x 50 mm)',
    shape: 'rectangular-block-3d',
    widthMm: 180,
    heightMm: 130,
    depthMm: 50,
    dpi: 600,
  },
  {
    id: 'square-block-3d-60x60x60',
    label: 'Square Block 3D - Small (60 x 60 x 60 mm)',
    shape: 'square-block-3d',
    widthMm: 60,
    heightMm: 60,
    depthMm: 60,
    dpi: 600,
  },
  {
    id: 'square-block-3d-100x100x60',
    label: 'Square Block 3D - Medium (100 x 100 x 60 mm)',
    shape: 'square-block-3d',
    widthMm: 100,
    heightMm: 100,
    depthMm: 60,
    dpi: 600,
  },
  {
    id: 'square-block-3d-140x140x60',
    label: 'Square Block 3D - Big (140 x 140 x 60 mm)',
    shape: 'square-block-3d',
    widthMm: 140,
    heightMm: 140,
    depthMm: 60,
    dpi: 600,
  },
  {
    id: 'rectangular-2d-plain-cut-80x60x20',
    label: 'Rectangular 2D Plain Cut - Small (80 x 60 x 20 mm)',
    shape: 'rectangular-2d-plain-cut',
    widthMm: 80,
    heightMm: 60,
    depthMm: 20,
    dpi: 600,
  },
  {
    id: 'rectangular-2d-plain-cut-90x130x20',
    label: 'Rectangular 2D Plain Cut - Medium (90 x 130 x 20 mm)',
    shape: 'rectangular-2d-plain-cut',
    widthMm: 90,
    heightMm: 130,
    depthMm: 20,
    dpi: 600,
  },
  {
    id: 'rectangular-2d-plain-cut-100x150x20',
    label: 'Rectangular 2D Plain Cut - Big (100 x 150 x 20 mm)',
    shape: 'rectangular-2d-plain-cut',
    widthMm: 100,
    heightMm: 150,
    depthMm: 20,
    dpi: 600,
  },
  {
    id: 'iceberg-130x100x50',
    label: 'Iceberg - Medium (130 x 100 x 50 mm)',
    shape: 'iceberg',
    widthMm: 130,
    heightMm: 100,
    depthMm: 50,
    dpi: 600,
  },
  {
    id: 'iceberg-175x135x50',
    label: 'Iceberg - Big (175 x 135 x 50 mm)',
    shape: 'iceberg',
    widthMm: 175,
    heightMm: 135,
    depthMm: 50,
    dpi: 600,
  },
  {
    id: 'regular-heart-3d-90x90x30',
    label: 'Regular Heart 3D - Medium (90 x 90 x 30 mm)',
    shape: 'regular-heart-3d',
    widthMm: 90,
    heightMm: 90,
    depthMm: 30,
    dpi: 600,
  },
  {
    id: 'regular-heart-3d-140x140x50',
    label: 'Regular Heart 3D - Big (140 x 140 x 50 mm)',
    shape: 'regular-heart-3d',
    widthMm: 140,
    heightMm: 140,
    depthMm: 50,
    dpi: 600,
  },
  {
    id: 'diamond-cut-heart-2d-90x90x20',
    label: 'Diamond Cut Heart 2D - Medium (90 x 90 x 20 mm)',
    shape: 'diamond-cut-heart-2d',
    widthMm: 90,
    heightMm: 90,
    depthMm: 20,
    dpi: 600,
  },
  {
    id: 'diamond-cut-heart-2d-140x140x20',
    label: 'Diamond Cut Heart 2D - Big (140 x 140 x 20 mm)',
    shape: 'diamond-cut-heart-2d',
    widthMm: 140,
    heightMm: 140,
    depthMm: 20,
    dpi: 600,
  },
  {
    id: 'circle-block-3d-140x120x40',
    label: 'Circle Block 3D - One Size (140 x 120 x 40 mm)',
    shape: 'circle-block-3d',
    widthMm: 140,
    heightMm: 120,
    depthMm: 40,
    dpi: 600,
  },
  {
    id: 'octagon-plaque-100x100x20',
    label: 'Octagon Plaque - Medium (100 x 100 x 20 mm)',
    shape: 'octagon-plaque',
    widthMm: 100,
    heightMm: 100,
    depthMm: 20,
    dpi: 600,
  },
  {
    id: 'octagon-plaque-180x180x20',
    label: 'Octagon Plaque - Big (180 x 180 x 20 mm)',
    shape: 'octagon-plaque',
    widthMm: 180,
    heightMm: 180,
    depthMm: 20,
    dpi: 600,
  },
];

const DEFAULT_ACCESSORY_PRESETS = [
  {
    id: 'accessory-rectangle-45x30',
    label: 'Accessory Rectangle - Small (45 x 30 mm)',
    shape: 'accessory-rectangle',
    widthMm: 45,
    heightMm: 30,
    dpi: 600,
  },
  {
    id: 'accessory-heart-35x35',
    label: 'Accessory Heart - Medium (35 x 35 mm)',
    shape: 'accessory-heart',
    widthMm: 35,
    heightMm: 35,
    dpi: 600,
  },
];

const requestedDefaultPresetId = (source, mode) => {
  const configured = String(source?.[mode]?.defaultPresetId || '').trim();
  if (configured) {
    return configured;
  }
  return String(DEFAULT_CRYSTAL_PROFILE_CATALOG?.[mode]?.defaultPresetId || '').trim();
};

export const CRYSTAL_PROFILE_MODES = [
  {
    mode: 'twoD',
    title: '2D Crystal Export Profiles',
    selectorLabel: '2D',
    depth: false,
  },
  {
    mode: 'threeD',
    title: '3D Crystal Size Profiles',
    selectorLabel: '3D',
    depth: true,
  },
  {
    mode: 'accessories',
    title: 'Accessories Profiles',
    selectorLabel: 'Accessories',
    depth: false,
  },
];

export const CRYSTAL_PROFILE_CONFIG_KEY = 'ui.crystal_profiles.v1';

export const DEFAULT_CRYSTAL_PROFILE_CATALOG = {
  version: 1,
  twoD: {
    defaultPresetId: 'rectangular-2d-plain-cut-90x130x20',
    presets: DEFAULT_TWO_D_PRESETS,
  },
  threeD: {
    defaultPresetId: 'rectangular-block-3d-120x80x50',
    presets: DEFAULT_THREE_D_PRESETS,
  },
  accessories: {
    defaultPresetId: 'accessory-rectangle-45x30',
    presets: DEFAULT_ACCESSORY_PRESETS,
  },
};

const normalizePreset = (rawPreset, mode, index) => {
  const defaultPrefix = mode === 'threeD' ? 'block' : mode === 'accessories' ? 'acc' : 'rect';
  const defaultId = `${defaultPrefix}_${index + 1}`;
  const fallbackWidth = mode === 'threeD' ? 50 : 50;
  const fallbackHeight = mode === 'threeD' ? 50 : 50;
  const fallbackDepth = mode === 'threeD' ? 20 : null;

  const widthMm = clampNumber(rawPreset?.widthMm, 1, 2000, fallbackWidth);
  const heightMm = clampNumber(rawPreset?.heightMm, 1, 2000, fallbackHeight);
  const dpi = clampNumber(rawPreset?.dpi, 72, 1200, 600);

  const preset = {
    id: sanitizeId(rawPreset?.id, defaultId),
    label: sanitizeLabel(rawPreset?.label, `${defaultPrefix.toUpperCase()} ${index + 1}`),
    shape: sanitizeLabel(rawPreset?.shape, 'rectangle').toLowerCase(),
    widthMm,
    heightMm,
    dpi,
  };

  const shapeDataUrl = normalizeShapeDataUrl(rawPreset?.shapePngDataUrl);
  if (shapeDataUrl) {
    preset.shapePngDataUrl = shapeDataUrl;
  }

  const clipMaskDataUrl = normalizeShapeDataUrl(rawPreset?.clipMaskPngDataUrl);
  if (clipMaskDataUrl) {
    preset.clipMaskPngDataUrl = clipMaskDataUrl;
  }

  const shapeBounds = normalizeShapeBounds(rawPreset?.shapeBoundsPx);
  if (shapeBounds) {
    preset.shapeBoundsPx = shapeBounds;
  }

  const ratioValue = Number(rawPreset?.shapeRatio);
  if (Number.isFinite(ratioValue) && ratioValue > 0.05 && ratioValue < 20) {
    preset.shapeRatio = ratioValue;
  } else if (shapeBounds) {
    preset.shapeRatio = Number((shapeBounds.width / shapeBounds.height).toFixed(6));
  }

  if (mode === 'threeD') {
    preset.depthMm = clampNumber(rawPreset?.depthMm, 1, 2000, fallbackDepth);
  }

  return preset;
};

const normalizeCatalogSection = (rawSection, defaults, mode) => {
  const basePresets = Array.isArray(rawSection?.presets) && rawSection.presets.length > 0
    ? rawSection.presets
    : defaults;

  const normalizedPresets = basePresets.map((preset, index) => normalizePreset(preset, mode, index));

  const dedupedPresets = [];
  const seenIds = new Set();
  normalizedPresets.forEach((preset, index) => {
    let nextId = preset.id;
    if (seenIds.has(nextId)) {
      nextId = `${nextId || mode}_${index + 1}`;
    }
    seenIds.add(nextId);
    dedupedPresets.push({ ...preset, id: nextId });
  });

  const defaultPresetId = String(rawSection?.defaultPresetId || '').trim();
  const resolvedDefault = dedupedPresets.some((preset) => preset.id === defaultPresetId)
    ? defaultPresetId
    : dedupedPresets[0].id;

  return {
    defaultPresetId: resolvedDefault,
    presets: dedupedPresets,
  };
};

export const normalizeCrystalProfileCatalog = (rawCatalog) => {
  const source = rawCatalog && typeof rawCatalog === 'object' ? rawCatalog : {};
  const sourceTwoD = source?.twoD && typeof source.twoD === 'object' ? source.twoD : {};
  const sourceThreeD = source?.threeD && typeof source.threeD === 'object' ? source.threeD : {};
  const sourceAccessories = source?.accessories && typeof source.accessories === 'object' ? source.accessories : {};

  const twoD = normalizeCatalogSection(
    {
      ...sourceTwoD,
      defaultPresetId: requestedDefaultPresetId(source, 'twoD'),
    },
    DEFAULT_TWO_D_PRESETS,
    'twoD'
  );
  const threeD = normalizeCatalogSection(
    {
      ...sourceThreeD,
      defaultPresetId: requestedDefaultPresetId(source, 'threeD'),
    },
    DEFAULT_THREE_D_PRESETS,
    'threeD'
  );
  const accessories = normalizeCatalogSection(
    {
      ...sourceAccessories,
      defaultPresetId: requestedDefaultPresetId(source, 'accessories'),
    },
    DEFAULT_ACCESSORY_PRESETS,
    'accessories'
  );

  return {
    version: 1,
    twoD,
    threeD,
    accessories,
  };
};

export const getModeCrystalCatalog = (catalog, is3DMode) => {
  const normalized = normalizeCrystalProfileCatalog(catalog);
  return is3DMode ? normalized.threeD : normalized.twoD;
};

export const findCrystalPreset = (catalogSection, presetId) => {
  if (!catalogSection || !Array.isArray(catalogSection.presets) || catalogSection.presets.length === 0) {
    return null;
  }

  const targetId = String(presetId || '').trim();
  if (targetId) {
    const found = catalogSection.presets.find((preset) => preset.id === targetId);
    if (found) {
      return found;
    }
  }

  return catalogSection.presets.find((preset) => preset.id === catalogSection.defaultPresetId) || catalogSection.presets[0] || null;
};

export const applyTwoDPresetToCrystalExport = (prevState, preset) => {
  if (!preset) {
    return prevState;
  }

  return {
    ...prevState,
    presetId: preset.id,
    shape: preset.shape || 'rectangle',
    widthMm: preset.widthMm,
    heightMm: preset.heightMm,
    dpi: preset.dpi,
  };
};

export const buildThreeDPhysicalPrefill = (preset) => {
  if (!preset) {
    return null;
  }

  return {
    presetId: preset.id,
    shape: preset.shape || 'rectangle',
    widthMm: clampNumber(preset.widthMm, 1, 2000, 50),
    heightMm: clampNumber(preset.heightMm, 1, 2000, 50),
    depthMm: clampNumber(preset.depthMm, 1, 2000, 20),
    dpi: clampNumber(preset.dpi, 72, 1200, 600),
    zMode: 'fixed_mm',
  };
};

const crystalProfiles = {
  CRYSTAL_PROFILE_CONFIG_KEY,
  CRYSTAL_PROFILE_MODES,
  DEFAULT_CRYSTAL_PROFILE_CATALOG,
  normalizeCrystalProfileCatalog,
  getModeCrystalCatalog,
  findCrystalPreset,
  applyTwoDPresetToCrystalExport,
  buildThreeDPhysicalPrefill,
};

export default crystalProfiles;
